import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Search, FileCheck, FileX, Clock, Eye, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: XCircle },
  info_requested: { label: "Info Requested", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
};

const roleTypeColors = {
  "Physiotherapist": "bg-primary-50 text-primary",
  "Occupational Therapist": "bg-blue-50 text-blue-700",
  "Speech Therapist": "bg-purple-50 text-purple-700",
  "Dietitian": "bg-emerald-50 text-emerald-700",
  "Caregiver": "bg-amber-50 text-amber-700",
};

export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/applications${params}`, { withCredentials: true });
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await axios.put(`${API}/admin/applications/${applicationId}`, {
        status: newStatus,
        admin_notes: adminNotes
      }, { withCredentials: true });
      
      toast.success(`Application ${newStatus}`);
      setDialogOpen(false);
      setAdminNotes("");
      fetchApplications();
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Failed to update application");
    }
  };

  const filteredApplications = applications.filter(app =>
    app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.role_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === "pending").length,
    approved: applications.filter(a => a.status === "approved").length,
    rejected: applications.filter(a => a.status === "rejected").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-applications">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Total Applications</p>
            <p className="text-2xl font-heading font-bold text-slate-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Pending Review</p>
            <p className="text-2xl font-heading font-bold text-amber-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Approved</p>
            <p className="text-2xl font-heading font-bold text-emerald-600">{stats.approved}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Rejected</p>
            <p className="text-2xl font-heading font-bold text-red-600">{stats.rejected}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="info_requested">Info Requested</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Applications ({filteredApplications.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto table-scroll">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Role Type</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied On</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApplications.map((app) => {
                  const StatusIcon = statusConfig[app.status]?.icon || Clock;
                  return (
                    <TableRow key={app.application_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${app.therapist_id}`} />
                            <AvatarFallback className="bg-primary text-white">
                              {app.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{app.name}</p>
                            <p className="text-sm text-slate-500">{app.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleTypeColors[app.role_type] || "bg-slate-100 text-slate-700"}>
                          {app.role_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {app.documents?.filter(d => d.uploaded).length || 0} / {app.documents?.length || 3}
                          {app.missing_documents?.length > 0 && (
                            <span className="text-xs text-red-500">
                              ({app.missing_documents.length} missing)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[app.status]?.color} flex items-center gap-1 w-fit`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig[app.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {new Date(app.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedApp(app);
                            setDialogOpen(true);
                          }}
                          data-testid={`view-app-${app.application_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Application Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="application-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Application Details</DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              {/* Applicant Info */}
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedApp.therapist_id}`} />
                  <AvatarFallback className="bg-primary text-white text-xl">
                    {selectedApp.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-heading font-semibold text-xl">{selectedApp.name}</p>
                  <p className="text-slate-500">{selectedApp.email}</p>
                  <Badge className={roleTypeColors[selectedApp.role_type] || "bg-slate-100"}>
                    {selectedApp.role_type}
                  </Badge>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Documents</h4>
                <div className="space-y-2">
                  {selectedApp.documents?.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {doc.uploaded ? (
                          <FileCheck className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <FileX className="w-5 h-5 text-red-500" />
                        )}
                        <span className="text-sm">{doc.type}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.verified && (
                          <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                        )}
                        {!doc.uploaded && (
                          <Badge className="bg-red-100 text-red-700">Missing</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Admin Notes</h4>
                <Textarea
                  placeholder="Add notes about this application..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  data-testid="admin-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedApp?.status === "pending" && (
              <>
                <Button
                  variant="outline"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => handleStatusUpdate(selectedApp.application_id, "info_requested")}
                  data-testid="request-info-btn"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Request Info
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleStatusUpdate(selectedApp.application_id, "rejected")}
                  data-testid="reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleStatusUpdate(selectedApp.application_id, "approved")}
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
